import { TRAIN_STATUS, DIALOG_EXPORT_KEY, LabelTranslations } from '@ai/common/src/types/expert';
import { EXPERT_TAB } from '@ai/uimain/src/type/expert';
import { DialogChartMetric, MessageChartMetric, Period, Unit } from '@constants/chartConstants';
import { VoiceGroup } from '@sections/expert-configuration/SpeechTab/SpeechTab';
import { ru } from './ru';
import { SystemSettings } from '@ai/common/src/types/config';
import { YmGoal } from '@ai/common/src/utils/yandexMetrikaUtils';

export const en: typeof ru = {
  title: 'AI Expert',
  version: 'Version',
  clickToCopy: 'Click to copy',
  copied: 'Copied',
  avatar: 'Avatar',
  random: 'Random',
  link: 'Link',
  imageUrl: 'Image URL',
  file: 'File',
  animated: 'Animated',
  add: 'Add',
  all: 'All',
  unknown: 'Unknown',
  noData: 'No data',
  example: 'Example',
  or: 'or',
  notProvided: 'not provided',

  goodMorning: 'Good morning',
  goodAfternoon: 'Good afternoon',
  goodEvening: 'Good evening',
  goodNight: 'Good night',

  save: 'Save',
  cancel: 'Cancel',
  download: 'Download',
  description: 'Description',
  expert: 'Expert',
  experts: 'Experts',
  student: 'Student',
  students: 'Students',
  comment: 'Comment',
  enter: 'Enter',
  error: 'Error',
  errorOccured: 'Error occured',

  goHome: 'Go to home',
  loading: 'Loading...',
  no: 'No',
  undefined: 'Undefined',
  yes: 'Yes',
  attention: 'Attention',
  language: 'Language',
  confirm: 'Confirm',
  searchText: 'Search by text',
  debug: 'Debug',
  delete: 'Delete',
  tokens: 'Tokens',
  cost: 'Cost',
  date: 'Date',
  content: 'Content',
  durationSec: 'Duration, sec',
  speechRecognitionSec: 'Recognition, sec',
  speechSynthesisSec: 'Synthesis, sec',
  speechSynthesisCount: 'Synthesis, chars',
  type: 'Type',
  placeholderExpertName: 'New expert',
  placeholderStudentName: 'New student',
  created: 'Created',
  pin: 'Pin',
  unpin: 'Unpin',
  continue: 'Continue',
  serverIsNotResponding: 'Server not responding, please try again later',
  serverRespond502: 'The service is temporarily unavailable',
  of: 'of',
  noOptions: 'No available options',

  validation: {
    requiredField: 'The field is required',
    bothRequiredOrNone: 'Both fields must be filled in or both should be empty.',
    maxLength: 'Max length - {{length}} characters',
    minLength: 'Minimum length - {{length}} characters',
    fieldError: 'Some fields filled incorrectly: {{fields}}.',
    notSupportedFormat: 'Unsupported format',
    fileIsTooLarge: 'Max size for file — {{size}}',
    fileUploadError: 'File upload error',
    invalidMcpServers: 'Invalid MCP server configuration',
    mcpServerNameRequired: 'Server name is required',
    invalidUrl: 'Invalid URL',
    keyRequiredForValue: 'Key Required',
  },

  expertFormLabels: {
    name: 'Expert name',
    image_id: 'Avatar',
    'config.system_prompt': 'System prompt',
    'config.system_prompt_voice_chat': 'Prompt for voice chat',
    'config.system_prompt_text_chat': 'Prompt for text chat',
    'config.use_url_context': 'Read link content',

    'config.first_sentense': 'First message',
    'config.temperature': '',
    'config.llm': '',
    'config.max_tokens': 'Maximum number of tokens',
    'config.rag_chunk_size': 'Context chunk size',
    'config.rag_chunk_count': 'Number of context chunks',
    'config.use_extended_doc_processing': 'Extended document processing',
    'config.use_image_vision': 'Vision image description',
    'config.show_answer_source': 'Show answer sources',
    'config.emotion': '',
    'config.voice': '',
    'config.speed': 'Speech speed',
    'config.stt_pause_between_words': 'Pauses between words',
    'config.before_start_text': 'Before starting the conversation',
    'config.listening_status_text': 'Expert is listening',
    'config.speaking_status_text': 'Expert is speaking',
    'config.no_interrupt_speaking_status_text': 'Expert is speaking in no-interrupt mode',
    'config.thinking_status_text': 'Expert is thinking',
    'config.case_sensitive_replacements': 'Abbreviations and terms',
    'config.case_insensitive_replacements': 'Long Words',
    'config.timezone': 'Timezone',
    'config.yandex_calendar.caldav_url': 'Calendar CalDAV URI',
    'config.yandex_calendar.caldav_password': 'Calendar password',
    'config.widget_size': 'Compact variant',
    'config.tg_tokens': 'Telegram bot tokens',
    'config.safe_interrupt': 'Safe interrupt',
    'config.is_published': 'Published',
    'config.google_sheets.spreadsheet_url': 'Spreadsheet URL',
    'config.google_sheets.columns_mapping': 'Displayed columns',
    'config.dialog_analisys_additional_fields': 'Describe additional fields',
    'config.suggested_questions': 'Starter suggestions',
    'config.use_suggested_questions': 'Auto-suggestions',
    'config.allow_profanity': 'Profanity allowed',
    'config.allow_emoji': 'Emoji allowed',
    'config.allow_offtop': 'Off-topic allowed',
    'config.proactive_triggers': 'Active invitations',
    'config.proactive_triggers.timer.message': 'Message text (timer)',
    'config.proactive_triggers.exit_intent.message': 'Message text (exit intent)',
    'config.proactive_triggers.scroll_depth.message': 'Message text (scroll depth)',
    'config.proactive_triggers.inactivity.message': 'Message text (inactivity)',
    'config.proactive_triggers.url_match.message': 'Message text (specific page)',
    'config.proactive_triggers.url_match.url': 'Page address (active invitation)',
    'config.allow_calling_telegram_operator': 'Allow calling telegram-operator via chat',
    'config.allow_calling_supervisor': 'Allow calling a support operator via chat',
  } satisfies LabelTranslations,

  timeFormat: {
    hour: 'h',
    min: 'min',
    min_short: 'm',
    sec: 'sec',
    sec_short: 's',
  },

  chat: {
    clearChat: 'Clear chat',
    enterMessage: 'Enter message',
    chatLanguage: 'Chat language',
    maxTokensError: 'Response exceeded limits and was cut off',
    prohibitedContentRequestError: 'Request contains prohibited topics',
    prohibitedContentResponseError: 'Response contains prohibited topics',
    simulateVoiceMode: 'Simulate voice mode',
    simulateVoiceModeDescr:
      'Simulates voice communication: responses are generated with a priority on speed.',
  },
  player: {
    pause: 'Pause',
    play: 'Play',
    error: 'Playback error',
    speechLength: 'Speech duration',
    signalNoise: 'Noise',
    reverberation: 'Reverberation',
    quality: 'Quality',
    recordMarkupAlgorithm: 'Markup algorithm',
    lowFrequencyBoundary: 'Low frequency',
    highFrequencyBoundary: 'High frequency',
    reverbMomentsCount: 'Number of reverberation moments',
    maxNotClippedSegmentLength: 'Maximum segment duration',
    errorPlayer: 'Player error',
  },
  login: {
    welcomeText:
      'A web service that allows you to create virtual AI experts based on advanced LLM (Large Language Models) and RAG (Retrieval-Augmented Generation) technologies.',
    welcomeSubtext:
      'AI Expert uses cutting-edge language model technology to provide deep analytical data and understanding of textual information. Designed for various applications, this tool offers unparalleled accuracy in text interpretation, sentiment analysis, and contextual understanding. Whether you are structuring data, extracting key ideas, or exploring complex narratives, AI Expert is designed to meet your linguistic analysis needs with precision and efficiency.',
    registration: 'Registration',
    accountQuestion: 'Do you already have an account?',
    enter: 'Sign in',
    or: 'or',
    title: 'Login',
    registerQuestion: "Don't have an account?",
    register: 'Register',
    signin: 'Sign in',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    passwordConfirm: 'Password again',
    agreeProcessingPersonalData: 'I agree to the processing of personal data',
    error: {
      wrongEmail: 'Please enter a valid email address',
      passwordMatch: 'Passwords must match',
      wrongEmailOrPassword: 'Incorrect email or password',
      noToken: 'User token is not defined',
      noEmail: 'User email is not defined',
      maxLength: 'Max length - {{length}} characters',
      userDisabled: 'User is disabled',
    },
    required: {
      username: 'Enter a username',
      email: 'Enter an email address',
      password: 'Enter a password',
    },
    logout: 'Logout',
    errorRegister:
      'An error occured during registration. Please check the data you provided and try again. If you already have an account, you can reset your password.',

    sendLink: 'Send link',
    resendLink: 'Resend link',
    linkError: 'The link is invalid or has expired. Please request a new verification link.',

    verificationLinkSent: 'A verification link has been sent to email {{email}}.',
    verificationSuccess: 'Email successfully verified! You can now log in.',

    forgotPassword: 'Forgot password?',
    goToAuth: 'Go to login',
    enterEmailForReset:
      "Enter the email you used during registration and we'll send you a password reset link.",
    resetLinkSent: 'A password reset link has been sent to email {{email}}.',
    createNewPassword: 'Create a new password for your account.',
    passwordResetSuccess: 'Password successfully changed!',
  },
  expertsList: {
    deleteExpert: 'Delete expert',
    deleteExpertConfirm:
      'Are you sure you want to delete the expert {{name}}?\n\nDeleting the expert will also remove all associated data, including dialogs and statistics.\n\nThis action cannot be undone.',

    duplicateExpert: 'Duplicate expert',
    duplicateExpertConfirm: 'Are you sure you want to duplicate the expert',
    duplicateTo: ' to the user ',
    duplicateSuccess: 'Expert «{{name}}» is successfully duplicated to user with ID {{userId}}',
    duplicateError: 'Error while duplicating expert',

    expertNotSelected: 'No expert selected',
    selectExpert:
      'Select an existing expert of create a new one to configure and test your voice assistant',
    createExpert: 'Create expert',
    openMenu: 'Open menu',
    advancedMode: 'Advanced mode',
    publishedDescr: 'If the expert is published, it will be added to all new users.',
    expertManagerInfo:
      'All your published experts will be automatically added to new users accounts as pre-installed examples.',
    published: 'Published',
    notPublished: 'In progress',
    useTools: 'Enable tools',
    useToolsDescr:
      'Upon activation, the expert will gain access to tools for searching the knowledge base, as well as the ability to connect MCP servers to expand the list of available tools.',

    expertStatus: {
      [TRAIN_STATUS.completed]: 'Trained',
      [TRAIN_STATUS.failed]: 'Error',
      [TRAIN_STATUS.in_progress]: 'Training',
      [TRAIN_STATUS.pending]: 'Not trained',
      unknown: 'Undefined',
    },
    expertTabs: {
      [EXPERT_TAB.EXPERT]: 'Expert',
      [EXPERT_TAB.TRAINING]: 'Training',
      [EXPERT_TAB.CHAT]: 'Chat',
      [EXPERT_TAB.WIDGET]: 'Widget',
      [EXPERT_TAB.HISTORY]: 'History',
      [EXPERT_TAB.SPEECH]: 'Speech',
      [EXPERT_TAB.INTEGRATIONS]: 'Integrations',
      [EXPERT_TAB.TOOLS]: 'Tools',
    },
    systemPromptDescr:
      "The system prompt is used to define the expert's character and conversation context.",
    useUrlContextDescr:
      'Enable to let the expert read and analyze the content of web pages from links in the message.',
    allowProfanityDescr:
      'If enabled, the expert may use profanity when appropriate. If disabled, profanity is strictly forbidden under any circumstances.',
    allowEmojiDescr:
      'If enabled, the expert may use emoji when appropriate or when explicitly asked. If disabled, emoji is strictly forbidden under any circumstances.',
    allowOfftopDescr:
      'If enabled, the expert can discuss any topic. If disabled, the expert strictly stays within their role and politely declines off-topic questions.',

    firstMessageDescr:
      'The first phrase the expert will say. If it is not set, the expert will wait for the user to start the conversation.',
    suggestedQuestionsDescr:
      'Questions or phrases suggested to the user when they first interact with the expert.',
    useSuggestedQuestionsDescr:
      'Automatically suggest follow-up options to the user after each expert response.',
    addQuestionPlaceholder: 'Enter a question...',
    generateQuestions: 'Generate',
    generateQuestionsDisabledHint:
      'To generate starter suggestions, fill in the expert system prompt first.',
    llmAndTemperature: 'LLM and temperature',
    llm: 'LLM',
    speed: 'Speed',
    intelligence: 'Intelligence',
    contextWindow: 'Context window',
    llmAndTemperatureDescr:
      'Select the language model and temperature to use.\nThe lower the temperature value,\
       the more deterministic the results will be. Increasing the temperature can lead to greater randomness,\
        which promotes more diverse or creative results.',
    llmDescr: 'Select the language model to use.',
    maxTokensDescr:
      'This setting determines the maximum amount of text the expert can generate in a single response.\
     The higher the value, the longer and more detailed the response can be, but the generation time will also increase.\
     \n\nA limit of 500 tokens is suitable for describing services or providing detailed answers to questions.\
      Values around 10000 tokens are suitable for creating very detailed texts, such as long articles or multi-step instructions.\
      \n\nOn average, one word in English takes up 1–1.5 tokens.',
    recognitionSettings: 'Recognition Settings',
    recognitionSettingsDescr:
      'These parameters define how the system distinguishes speech from silence or background noise. Precise tuning helps improve recognition quality and speed.',
    pausesBetweenWordsDescr:
      'This parameter controls the expected duration (in milliseconds) of pauses between words within phrase. With this parameter, you can avoid incorrect phrase end detection when a speaker is dictating numbers slowly or set up how fast the voice assistant should respond to the end of speech.',

    voiceAndCharacter: 'Voice and persona',
    voiceDescr: 'Choose voice and speaking style of the expert.',
    voiceGroups: {
      [VoiceGroup.YANDEX]: 'Голоса Яндекс',
      [VoiceGroup.GOOGLE]: 'Голоса Google',
      [VoiceGroup.ELEVENLABS]: 'Голоса ElevenLabs',
      otherVoices: 'Другие голоса',
    },

    embedCode: 'Embed code',
    embedCodeDescr: 'Add the following snippet to the pages where you want to place the widget.',
    avatarDescr: 'Select one of the random avatars or add your own image.',
    randomDescr: 'Click the avatar to change the image.',
    texts: 'Texts',
    textsDescr: 'Edit the texts displayed in the widget.',

    speedDescr: 'The speed at which the expert speaks.',
    endPhrasesDescr:
      "If one of these phrases present in expert's line, this line will be considered the last\
     one in current dialog, so the dialog will end after expert finishes speech, unless user interrupts.",
    contextSettings: 'Context and search settings',
    contextSettingsDescr:
      'Configure how the system processes and uses information to generate responses.',
    ragChunkSizeDescr:
      'This setting determines the size of data chunks (in characters) used to build the context.\
\n\nIf the size is too small, it may result in loss of semantic coherence.\
\nIf too large, it can introduce excessive information, reduce relevance, and slow down responses.\
\nThe optimal size depends on the data structure and query type.\
\n\nChanging this setting requires retraining the expert.',
    ragChunkCountDescr:
      'This setting determines how many data fragments will be added to the context for generating a response. The more fragments used, the more accurate and detailed the response will be, but this also increases the system load and processing time.',
    extendedDocProcessingDesc:
      'Use advanced document processing tools with the capability for image extraction and OCR functions.',
    imageVisionDesc:
      'Analyze images in documents and uploaded image files using a vision model. The text description of images will be saved to the knowledge base and available for semantic search.\n\nChanging this setting requires retraining the expert.',
    imageVisionModelDesc: 'Select the vision model for image analysis.',
    imageVisionDisabledTooltip: 'Enable Vision mode in advanced settings to train on this file',
    documentStatus: {
      pending: 'Pending',
      in_progress: 'Processing',
      completed: 'Success',
      failed: 'Error',
    },
    useElasticSearchDescr:
      'Enabling this option improves recognition of abbreviations, names, and specialized terms in the text. However, it may increase the request processing time.',
    showAnswerSourceDescr:
      'Show the names and text snippets of the original documents in each expert response.\nThis increases transparency and allows you to verify the context of the answer.',
    soundTest: 'Audio Preview',
    soundTestDescr: 'Listen to how the voice will sound with current settings.',
    listen: 'Listen',
    textForSynthesis: 'Text for synthesis',
    widgetSizeDescr: 'Enable this option to make the widget smaller.',
    proactiveTriggers: {
      sectionTitle: 'Active invitations',
      sectionDescr:
        'When embedded on your website, the widget will automatically open the chat and send a personalised message from the expert at the right moment. This helps increase conversion and user engagement — for example, offering help to someone who has been browsing for a while, catching a visitor who is about to leave, or greeting users on a specific page.\n\nOnly works when the widget is embedded via script. The first condition met triggers the invitation; all others are automatically cancelled.',
      messageLabel: 'Message text',
      testButton: 'Test',
      cooldownLabelStart: 'Show at most once every',
      cooldownLabelEnd: 'hours',
      timer: {
        title: 'By timer',
        descrStart: 'Chat opens after',
        descrEnd: 'seconds following page load.',
        delayLabel: 'Delay (seconds)',
        messagePlaceholder: 'Hi there! Happy to answer any questions 👋',
      },
      exit_intent: {
        title: 'About to leave the page',
        descr: 'Cursor exits the browser window, or fast upward scroll on mobile.',
        messagePlaceholder: "Wait — maybe I can help you find what you're looking for!",
      },
      scroll_depth: {
        title: 'Scroll depth',
        descrStart: 'Chat opens when the user has scrolled',
        descrEnd: '% or more of the page.',
        messagePlaceholder: "Looks like you're exploring — got any questions? I'm here!",
      },
      inactivity: {
        title: 'Inactive for N seconds',
        descrStart: 'No actions on the page for',
        descrEnd: 'seconds.',
        delayLabel: 'Inactivity time (seconds)',
        messagePlaceholder: 'Looking at your options? I can help you choose!',
      },
      url_match: {
        title: 'On a specific page',
        descr: 'The page address contains the specified text.',
        urlLabel: 'Page address',
        messagePlaceholder:
          'Interested in this topic? I can tell you more and answer any questions',
      },
    },
    safeInterruptDescr:
      'Disable interruption on devices prone to echo or self-triggering (Apple devices and mobile Firefox).',
    pronunciation: {
      title: 'Pronunciation and Stress',
      descr:
        'You can specify the correct pronunciation of names and abbreviations.\
  \n\n• Mark stress\nPlace a "+" sign before stressed vowels to indicate stress.\
  \nFor example: reference → r+eference\
  \n\n• Adjust spelling\nTry spelling words as they sound.\
  \nFor example: misfortune → misf+ortchune',
      caseSensitiveDescr:
        'Replacement will occur only with exact case matching (this prevents false replacements within other words)',
      caseInsensitiveDescr:
        'Replacement will occur regardless of case (to handle various forms of the same word).',
      word: 'Word',
      pronunciation: 'Pronunciation',
    },

    yandexCalendar: {
      title: 'Yandex Calendar',
      descr: 'Set up event creation in Yandex.Calendar.',
      descrVerbose:
        "If, during a conversation with the user, the expert schedules a meeting or a call and gets user's contatcs (phone or email), an event will be created in the specified calendar\
with a title in the format 'expert_name. user_name. creation_date',\
containing the user's contact details, task description, and other relevant information from the conversation.\
\n\nTo use this feature, you must explicitly include an instruction in the expert's system prompt to handle\
requests for scheduling meetings and calls, ask for contacts. \
\n\nIf the user schedules multiple meetings at once,\
the system may interpret this as a single request and create only one event.",
      uriForm: 'URI of the form ',
      caldavUrlInstruction:
        "1. In the list of calendars, click the ⚙ icon next to the desired calendar\
              \n2. Go to the 'Export' tab\
              \n3. Copy the contents of the CalDAV field",

      passwordDescr: 'A special password that grants access only to the calendar',
      followLink: 'Follow the link ',
      createPassword: " and create a new password for the 'Calendar' app",
      timezoneDescr: 'The timezone in which calendar events will be created',
    },

    googleSheets: {
      title: 'Google Sheets',
      descr: 'Configure export of conversation results to your Google Sheet.',
      columnName: 'Column name',
      columnsMappingDescr:
        'Select which data you want to export to your Google Sheet. \
    For each field, specify the exact column name from the first row of your sheet — values will be written there.\
\n\nIf the name is not provided or the column doesn’t exist in the sheet, the field’s data will not be exported.\
 You can also exclude a field from export by clicking the “Hide column” button.',

      dataKey: {
        [DIALOG_EXPORT_KEY.customer_email]: {
          title: 'User email',
          descr: 'User email obtained from the dialog',
        },
        [DIALOG_EXPORT_KEY.customer_name]: {
          title: 'User name',
          descr: 'User name obtained from the dialog',
        },
        [DIALOG_EXPORT_KEY.customer_phone]: {
          title: 'User phone number',
          descr: 'User phone number obtained from the dialog',
        },
        [DIALOG_EXPORT_KEY.customer_tz]: {
          title: 'User timezone',
          descr: 'Timezone from which the customer conducted the dialog',
        },
        [DIALOG_EXPORT_KEY.dialog_id]: { title: 'dialog ID', descr: '' },
        [DIALOG_EXPORT_KEY.event_datetime]: {
          title: 'Event date',
          descr: 'Date of the meeting or call scheduled during the dialog',
        },
        [DIALOG_EXPORT_KEY.expert_id]: { title: 'Expert ID', descr: '' },
        [DIALOG_EXPORT_KEY.task_short]: {
          title: 'User request (brief)',
          descr: 'Brief description of the customer’s request obtained from the dialog',
        },
        [DIALOG_EXPORT_KEY.task_verbose]: {
          title: 'User request (detailed)',
          descr: 'Detailed description of the customer’s request obtained from the dialog',
        },
        [DIALOG_EXPORT_KEY.transcription]: {
          title: 'Dialog transcript',
          descr: 'Full dialog text',
        },
        [DIALOG_EXPORT_KEY.created_at]: { title: 'Date', descr: 'Record creation date' },
        [DIALOG_EXPORT_KEY.field_1]: { title: 'Field 1', descr: 'Additional field' },
        [DIALOG_EXPORT_KEY.field_2]: { title: 'Field 2', descr: 'Additional field' },
        [DIALOG_EXPORT_KEY.field_3]: { title: 'Field 3', descr: 'Additional field' },
        [DIALOG_EXPORT_KEY.field_4]: { title: 'Field 4', descr: 'Additional field' },
        [DIALOG_EXPORT_KEY.field_5]: { title: 'Field 5', descr: 'Additional field' },
      },
      showColumn: 'Show column',
      hideColumn: 'Hide column',
      spreadsheetUrlDescr:
        'To enable the integration, you must grant editor access to the AI platform’s service account in the spreadsheet’s sharing settings.',
    },

    metrika: {
      title: 'Yandex Metrica',
      descr: 'Configure sending Yandex Metrica goals to track widget events.',
      descrVerbose:
        'To do this, you need to:\
    \n1. Go to the desired counter → Goals → Add goal\
    \n2. In the goal creation window, select "JavaScript event" as the condition type,\
     and in the goal identifier select "matches" and specify the goal you want to track.\
    \n\nAvailable event identifiers for Yandex Metrica:',

      [YmGoal.nlab_text_dialog_started]: {
        title: 'User started text dialog',
        descr: 'User clicked the text chat button.\nShows interest in interaction.',
      },
      [YmGoal.nlab_voice_dialog_started]: {
        title: 'User started voice dialog',
        descr:
          'User clicked the microphone button to start a voice dialog.\nReflects willingness to communicate by voice.',
      },
      [YmGoal.nlab_user_first_message_sent]: {
        title: 'User sent first message',
        descr: 'User typed or spoke the first message.\nFact of real dialog start.',
      },
      [YmGoal.nlab_switched_to_text_mode]: {
        title: 'User switched to text mode',
        descr:
          'User sent a text message in a voice chat.\nA high percentage may indicate that voice responses are too complex to perceive by ear or contain details the user wants to see in writing (numbers, links, instructions).',
      },
      [YmGoal.nlab_switched_to_voice_mode]: {
        title: 'User switched to voice mode',
        descr:
          'User sent a voice message in a text chat.\nA high percentage may indicate that text input is not always convenient for current usage scenarios — especially on mobile devices or with complex queries.',
      },
      [YmGoal.nlab_user_returned_to_dialog]: {
        title: 'User returned to dialog after time',
        descr: 'Message sent after the previous session ended.\nIndicates retained interest.',
      },
      [YmGoal.nlab_operator_requested]: {
        title: 'User called Telegram operator',
        descr:
          'AI expert determined that the user wants to communicate with an operator.\nAllows tracking the frequency of such requests and building interaction funnels.',
      },
      // [YmGoal.nlab_operator_joined_dialog]: {
      //   title: 'Telegram operator joined the dialog',
      //   descr:
      //     'Connection opened between a free Telegram operator and the user.\nRecords the fact of human participation in the dialog, helps track support team workload.',
      // },
      [YmGoal.nlab_supervisor_joined_dialog]: {
        title: 'Supervisor joined the dialog',
        descr:
          'Supervisor sent a message to the dialog.\nFrequency of supervisor connections helps assess query complexity and the need for dialog quality control.',
      },
    },

    documents: 'Documents',
    documentsDescr:
      'Provide the LLM with domain-specific information to help it answer questions more accurately.',
    noDocumentsAdded: 'No documents are added',
    trainExpert: 'Train expert',
    train: 'Train',
    loadingFile: 'Loading file...',
    documentsDndTitle: 'Drag documents or click to select files.',
    deleteDocumentConfirm:
      'Are you sure you want to delete the document {{title}}? \n\nDeleting the document will require complete retraining of the expert. \n\nThis action cannot be undone.',
    notSupportedFormat: 'Unsupported format',
    supportedFormats: 'Supported formats',

    imageDndTitle: 'Drag an image or click to select a file.',
    usageStatistics: 'Usage statistics',

    mcpServers: 'MCP Servers',
    mcpServersDescr:
      'Connect external MCP servers to extend the expert with additional tools. \
The expert will use them automatically during conversations.',
    mcpServersEmpty: 'No MCP servers added.',
    mcpServerName: 'Server name',
    mcpServerNamePlaceholder: 'my_server',
    mcpServerUrl: 'URL',
    mcpServerTransport: 'Transport',
    mcpServerAdd: 'Add server',

    tgOperator: 'Telegram Operator',
    tgOperatorDescr:
      'The user can switch from chatting with the AI expert to talking with a human operator \n\
by asking expert for this in the chat.',
    tgTokensDescr:
      'Create one or more Telegram bots and enter their tokens here.\n\
Each bot represents a dedicated communication channel between an operator and a single widget user.\n\
Multiple bots mean multiple parallel channels available for different users.\n\
When a user switches to an operator, they occupy one channel, making it unavailable until the conversation ends.\n\
If all channels are busy, other users cannot switch to an operator until one of the channels becomes free.',

    allowCallingOperator: 'Call a support operator via chat',
    allowCallingOperatorDescr:
      'The user can request an expert in the chat to call a support operator, \
     who will then initiate the conversation on behalf of the expert. \
    \nEnable if you want to allow users to call an operator',
  },

  dashboard: {
    dashboard: 'Dashboard',
    allExperts: 'All experts',
    allUsers: 'All users',
    chooseExpert: 'Choose an expert',

    period: {
      [Period.LAST_DAY]: 'Last day',
      [Period.LAST_WEEK]: 'Last week',
      [Period.LAST_MONTH]: 'Last month',
      [Period.LAST_YEAR]: 'Last year',
    },

    metric: {
      [DialogChartMetric.TOTAL_DIALOGS]: 'Number of dialogs',
      [DialogChartMetric.VOICE_DIALOGS]: 'Voice dialogs',
      [DialogChartMetric.TEXT_DIALOGS]: 'Text dialogs',
      [DialogChartMetric.VOICE_DIALOGS_AVERAGE_DURATION]: 'Average duration',
      // [DialogChartMetric.TOTAL_COST]: "Total cost",
      // [DialogChartMetric.AVERAGE_COST]: "Average cost",
      [DialogChartMetric.TOTAL_DURATION]: 'Total duration',
      [MessageChartMetric.TOTAL_RESPONSES]: 'Total responses',
      [MessageChartMetric.TEXT_RESPONSES]: 'Text responses',
      [MessageChartMetric.VOICE_RESPONSES]: 'Voice responses',
    },

    mostPopularExpert: 'Most popular expert',
  },

  chatHistory: {
    chatHistory: 'Dialog history',
    dialogType: 'Dialog type',
    period: 'Period',
    conversations: 'Conversations',
    chats: 'Chats',
    duration: 'Duration',
    allDialogs: 'All',
    noTextDataForChat: 'Not enough data to display chat',
    noUserDataForChat: 'No user query',
    messagesCount: 'Messages count',

    utmParameters: 'UTM-parameters',
    utmSource: 'Source',
    utmMedium: 'Medium',
    utmCampaign: 'Campaign',
    utmContent: 'Content',
    utmTerm: 'Term',
    ip: 'IP',

    activeDialogs: 'Active dialogs',
    operatorAnswering: 'Telegram operator is answering in the chat',
    supervisorAnswering: 'Operator is answering in the chat',
    expertAnswering: 'AI expert is answering in the chat',
    releaseDialog: 'Leave chat',

    activeDialogsAlertTitle: 'Dialogs with activity in the last 30 minutes',
    activeDialogsAlertDescr:
      'You can monitor conversations in real time or join them — once you send a message, the AI expert will stop responding and the conversation will be yours to handle. Enable the toggle to receive calls from users who requested an operator.',
    supervisorOnlineDescription:
      'You are accepting requests. You will receive a browser notification for each new request and can join the dialog',
    supervisorOfflineDescription:
      'You are not accepting requests. Enable to receive notifications and help users',
  },

  pricing: {
    pricing: 'Pricing',
    ofVoiceResponces: 'voice responses',
    ofTextResponces: 'text responses',
    cost: 'Cost',
    duration: 'Duration',
    left: 'Remaining',
    analytics: 'Analytics',
    purchasedAt: 'Purchased at',
    expiresAt: 'Expires at',
    allPlansExpiredActivateNew:
      'All your active plans have expired. To continue working with experts activate new ',
    toStartActivatePlan: 'To start working with experts, activate new ',
    suspiciousActivityActivateNew:
      'The tariff has been blocked due to suspicious activity. To continue working with experts, please activate new ',
    suspiciousActivity: 'The tariff has been blocked due to suspicious activity.',
    tariffPlan: 'tariff plan',
    allPlansExpired: 'All your active plans have expired.',
    toContinueContactUs: 'To continue working with experts, please contact us',
    toStartContactUs: 'To start working with experts, please contact us ',
    viaPhone: 'by phone ',
    orEmail: ' or by email ',
    andGetTrialForFree: 'and get trial access for free',
    purchasedTariffPlans: 'Purchased tariff plans',
    expiredPlans: 'Expired plans',
    currentPlan: 'Active plan',

    pricingDescr: 'You can interact with the expert in two ways:',
    voiceChat: 'Voice chat',
    voiceChatDescr: 'receive expert responses in real-time through voice communication.',
    textChat: 'Text chat',
    textChatDescr: 'ask questions and receive precise responses in text format.',
    pricingDescr2:
      'Depending on the communication method used, either voice or text responses are deducted,\
       while the number of responses of the other type is proportionally reduced.\
       \n Once you exhaust your limit, you can purchase an additional package or choose a different plan.',
    setTariff: 'Set tariff',
    current: 'Current',
    activePlans: 'Tariffs',
  },

  units: {
    [Unit.SECONDS]: 's',
  },

  dateSelector: {
    resetCalendar: 'Reset',
    allPeriod: 'All',
    yesterday: 'Yesterday',
    today: 'Today',
    calendar: 'Calendar',
    from: 'From',
    to: 'To',
  },
  users: {
    users: 'Users',
    registrationDate: 'Registration date',
    lastLogin: 'Last login',
    currentPurchaseExpiresAt: 'Current tariff expires at',

    searchPlaceholder: 'Search by name or e-mail',
    searchRefPlaceholder: 'Search by referrer',

    allUsers: 'All users',
    active: 'Active',
    disabledUsers: 'Disabled',
    disabledUser: 'Disabled',
    ref: 'Referrer',
  },

  queryDetailsInfo: {
    title: 'Response Formation Details',
    close: 'Close',
    userQuery: 'User Query:',
    dataSources: 'Data Sources',
    source: 'Source:',
    file: 'File:',
    score: 'Score:',
    distance: 'Distance',
    systemInstructions: 'System Instructions',
    metadata: 'Metadata',
    finalJsonPrompt: 'Final JSON Request (raw_prompt)',
    sourceType: {
      elk: 'ELK',
      vector: 'VECTOR',
      db: 'HISTORY',
    },
  },

  systemSettingsFormLabes: {
    welcome_tariff_id: 'Welcome tariff',
    stt_vad_threshold: 'VAD sensitivity threshold',
    stt_vad_speech_pad_ms: 'Audio padding at segment edges',
    system_prompt_constraints: 'System constraints',

    system_prompt_rag: 'Prompt for documents',
    system_prompt_rag_priority: 'Prompt for documents with knowledge base priority',
    system_prompt_images: 'Prompt for images',
    system_prompt_tg_operator_text_chat: 'Prompt for working with Telegram-bots in text mode',
    system_prompt_tg_operator_voice_chat: 'Prompt for working with Telegram-bots in voice mode',
    system_prompt_tg_operator_disabled:
      'Prompt for working with Telegram bots when Telegram bots are not configured',
    system_prompt_dialog_analysis: 'Prompt for dialog analysis',
    system_prompt_dialog_summary_for_operator:
      'Prompt for dialog summarization for a Telegram operator',
    system_prompt_emoji: 'Prompt for emojis',
    system_prompt_emoji_forbidden: 'Emoji forbidden prompt',
    system_prompt_offtop_forbidden: 'Off-topic restriction prompt',
    system_prompt_profanity_forbidden: 'Profanity forbidden prompt',
    system_prompt_profanity_allowed: 'Profanity allowed prompt',
    system_prompt_dialog_finished: 'Prompt for determining whether the dialog is finished',
    system_prompt_need_operator: 'Prompt for determining whether an operator is required',
    system_prompt_prioritize_operator: 'Operator response prioritization prompt',
    image_vision_model: 'Image analysis model',
    system_prompt_generate_starter_suggestions: 'Prompt for generating starter questions',
    system_prompt_generate_followup_suggestions: 'Prompt for generating follow-up suggestions',
    system_prompt_image_vision: 'Prompt for image description (Vision)',
    system_prompt_channel_rules: 'Base channel rules',
    system_prompt_behavior_settings: 'Base behavior settings',
    system_prompt_agent_role_preamble: 'Role preamble',
    system_prompt_default_role: 'Default role',
    system_prompt_knowledge_base_context: 'Base knowledge base context',
    system_prompt_reminder: 'System reminder',
    system_prompt_tools_constraints: 'Tools constraints',
    system_prompt_tools_behavior: 'Tools behavior',
    system_prompt_tools_knowledge_base: 'Knowledge base navigation',
    execution_profile_pipeline: 'PIPELINE profile',
    execution_profile_hybrid: 'HYBRID profile',
    execution_profile_agentic: 'AGENTIC profile',
  } satisfies Required<Record<keyof SystemSettings, string>>,

  settings: {
    settings: 'System settings',
    recognitionSettings: 'Recognition settings',
    recognitionSettingsDescr:
      'Configure the settings for optimal VAD and speech recognition performance.',
    defaultTariffDescr:
      'You can select a tariff plan that will be automatically assigned to new users upon registration.',
    vadThresholdDescr:
      'The higher the threshold, the fewer noises are let through, which improves recognition accuracy. However, there is a higher risk of missing quiet or indistinct phrases, especially with a low-quality microphone.',
    vadSpeechPadDescr:
      'How many additional milliseconds of audio will be added to the beginning and end of each detected speech segment.',

    executionProfilesTitle: 'Execution profiles',
    executionProfilesDescr:
      'Parameters for PIPELINE / HYBRID / AGENTIC modes. The verification_strategy field is computed automatically from the channel and is not stored here.',
    additionalPromptsTitle: 'Additional prompts for messages',
    additionalPromptsDescr:
      'Describe additional instructions for different scenarios here to generate more accurate and relevant responses.',
    systemPromptVoiceChatDescr:
      'This prompt is added to the LLM request when sending a voice message. Focused on correct speech synthesis.',
    systemPromptTextChatDescr:
      'This prompt is added to the LLM request when sending a text message. Focused on displaying formatted data in the chat.',
    systemPromptImagesDescr:
      'This prompt is added to the LLM request if the expert has the "Advanced document processing" option enabled, which includes image processing.',
    systemPromptForDocumentsDescr:
      'This prompt is added to the LLM request if at least one RAG chunk is included in the request.',
    systemPromptRagPriorityDescr:
      'This prompt replaces the standard RAG prompt when the expert has link reading enabled and a Gemini model is selected.',
    systemPromptTgOperatorTextChatDescr:
      'This prompt is added to the LLM request when sending a text message if Telegram bots are linked to the expert.',
    systemPromptTgOperatorVoiceChatDescr:
      'This prompt is added to the LLM request when sending a voice message if Telegram bots are linked to the expert.',
    systemPromptTgOperatorDisabledDescr:
      'This prompt is added to the LLM request if the user asks to connect an operator, but no Telegram bots are linked to the expert.',

    servicePromptsTitle: 'Prompts for service requests',
    servicePromptsDescr: 'Configure prompts for internal system requests.',
    systemPromptDialogAnalysisDescr:
      'Prompt for extracting data from the dialog.\nThe CURRENT_DATETIME variable is injected in the code to correctly determine relative time.\nThe ADDITIONAL_FIELDS variable represents additional fields calculated during Google Sheets integration.\nChange these variables only if you are absolutely sure what you are doing.',
    systemPromptDialogSummaryForOperatorDescr:
      'When a dialog is switched to a Telegram operator, the operator will receive a message with a brief summary of the previous dialog.',
    systemPromptConstraintsDescr:
      'Instruction hierarchy, injection protection, system data confidentiality, and prohibition on hallucination. Added first in the system prompt.',
    systemPromptEmojiDescr:
      'Applied when the "Emoji allowed" toggle is on. Allows emoji usage when appropriate or when explicitly asked.',
    systemPromptEmojiForbiddenDescr:
      'Applied when the "Emoji allowed" toggle is off. Strictly prohibits emoji usage under any circumstances.',
    systemPromptOfftopForbiddenDescr:
      'Applied when the "Off-topic allowed" toggle is off and the expert has a system prompt defined. Restricts the expert to their specialization.',
    systemPromptProfanityForbiddenDescr:
      "Applied when the expert's 'Profanity allowed' toggle is off. Absolute prohibition on profanity.",
    systemPromptProfanityAllowedDescr:
      "Applied when the expert's 'Profanity allowed' toggle is on. Permits profanity without encouraging it.",
    systemPromptDialogFinishedDescr:
      'If True is returned, dialog analysis is triggered, and its results are used to create a calendar event and other integrations. In voice mode, the expert stops the recording independently.',
    systemPromptNeedOperatorDescr:
      'If True is returned, the process of connecting an operator is initiated',
    systemPropmptPrioritizeOperatorDescr:
      'Instructs the AI expert to treat human responses (from an operator) as the authoritative reference.',
    systemPromptGenerateStarterSuggestionsDescr:
      'Prompt for generating 3 starter questions shown to the user when they first open the chat.',
    systemPromptGenerateFollowupSuggestionsDescr:
      'Prompt for generating 2-3 follow-up options suggested to the user after each expert response.',
    systemPromptImageVisionDescr:
      'Prompt for describing images when indexing documents (RAG). Used by the vision model to generate a text description of an image.',
    systemPromptAgentRolePreambleDescr:
      'Added first to the <agent_role_and_goal> section unconditionally. Tells the model that the expert role follows.',
    systemPromptDefaultRoleDescr:
      'Added instead of the expert role when no system prompt is defined in the expert settings.',
    systemPromptChannelRulesDescr:
      'Added first to the <channel_rules> section of the system prompt unconditionally, regardless of expert settings.',
    systemPromptBehaviorSettingsDescr:
      'Added first to the <behavior_settings> section of the system prompt unconditionally, regardless of expert settings.',
    systemPromptKnowledgeBaseContextDescr:
      'Added first to the <knowledge_base_context> section of the system prompt unconditionally, regardless of training status and expert settings.',
    systemPromptReminderDescr:
      'A short reminder added at the end of the system prompt — to stay in role and follow the rules.',

    toolsPromptsTitle: 'Prompts for tools mode',
    toolsPromptsDescr:
      'Additional instructions for experts with tools enabled (use_tools). The text is semantically split into three parts — each is inserted into the corresponding system prompt block: system_constraints, behavior_settings, and knowledge_base_context.',
    systemPromptToolsConstraintsDescr:
      'Hard prohibitions and constraints when working with tools. Inserted into the <system_constraints> block of the system prompt.',
    systemPromptToolsBehaviorDescr:
      'Behavioral patterns and rules for working with tools. Inserted into the <behavior_settings> block of the system prompt.',
    systemPromptToolsKnowledgeBaseDescr:
      'Instructions for navigating the knowledge base: document_id usage, traversal strategy, image handling. Inserted into the <knowledge_base_context> block of the system prompt.',
  },

  docs: {
    docs: 'Documentation',
  },

  support: { support: 'Tech support' },

  experimentalFeature: 'Experimental function',
};
